import { 
  MessageSquare, 
  Users, 
  BarChart3, 
  Shield, 
  Infinity,
  Headphones
} from "lucide-react";
import { motion } from "framer-motion";

const features = [
  {
    icon: MessageSquare,
    title: "WhatsApp + Instagram",
    description: "Centralize todas as conversas em um só lugar. Nunca mais perca um lead.",
    color: "cyan" as const,
  },
  {
    icon: Infinity,
    title: "Conexões Ilimitadas",
    description: "Conecte quantos números precisar. Cada filial, cada vendedor, sem custo extra.",
    color: "purple" as const,
  },
  {
    icon: Users,
    title: "Equipe Inteira Inclusa",
    description: "Adicione todos os atendentes sem pagar por cabeça. Um preço fixo, time ilimitado.",
    color: "cyan" as const,
  },
  {
    icon: Headphones,
    title: "Atendimento Eficiente",
    description: "Distribua conversas, acompanhe tempos de resposta e mantenha a qualidade.",
    color: "purple" as const,
  },
  {
    icon: Shield,
    title: "Blindado Contra Ban",
    description: "Com a API Oficial Meta, seu número nunca será banido. Segurança total.",
    color: "cyan" as const,
  },
  {
    icon: BarChart3,
    title: "Métricas em Tempo Real",
    description: "Saiba como sua equipe performa. Tempo de resposta, satisfação, conversões.",
    color: "purple" as const,
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

const FeaturesSection = () => {
  return (
    <section id="recursos" className="py-16 md:py-24 relative" aria-labelledby="features-heading">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-surface-card/50 to-transparent" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-3xl mx-auto mb-12 md:mb-16"
        >
          <h2 id="features-heading" className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-display font-bold mb-4 md:mb-6">
            Tudo para{" "}
            <span className="text-gradient-zavvi">escalar seu atendimento</span>
          </h2>
          <p className="text-base md:text-lg text-body-text px-4">
            Gestão omnichannel completa. Chega de perder leads por falta de estrutura.
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"
        >
          {features.map((feature) => (
            <motion.div
              key={feature.title}
              variants={itemVariants}
              whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
              className={`glass-card p-5 md:p-6 rounded-2xl transition-all duration-300 group ${
                feature.color === "cyan" ? "hover:neon-border-cyan" : "hover:neon-border-purple"
              }`}
            >
              <div
                className={`w-10 md:w-12 h-10 md:h-12 rounded-xl flex items-center justify-center mb-3 md:mb-4 transition-all duration-300 ${
                  feature.color === "cyan"
                    ? "bg-cyber-cyan/10 text-cyber-cyan group-hover:glow-cyan"
                    : "bg-electric-purple/10 text-electric-purple group-hover:glow-purple"
                }`}
              >
                <feature.icon size={20} className="md:w-6 md:h-6" />
              </div>
              <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3 text-foreground">{feature.title}</h3>
              <p className="text-sm md:text-base text-body-text">{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturesSection;
